import type {
  ColleagueListItem,
  ColleagueRelationship,
  OneOnOneColleagueDetail,
  OneOnOneColleagueItem,
  OneOnOneMeetingItem,
  OneOnOneMemory,
  OneOnOneMemoryFact,
  OneOnOneMemorySource,
} from '@wundo/shared/colleagues.ts';
import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';

export type {
  ColleagueListItem,
  ColleagueRelationship,
  OneOnOneColleagueDetail,
  OneOnOneColleagueItem,
  OneOnOneMeetingItem,
  OneOnOneMemory,
  OneOnOneMemoryFact,
  OneOnOneMemorySource,
};

/*
 * One request is enough for anybody's colleagues. This is the people you meet
 * with, not a directory — searching and sorting happen in the page, on a list
 * that fits in a hand.
 */
const COLLEAGUE_LIMIT = 200;

/**
 * The people the user meets with, or the ones they have hidden.
 *
 * Addressed by user id like the rest of the calendar: the same route serves a
 * superadmin looking at someone's page, and the policy decides who may, not the
 * path.
 */
export async function fetchColleagues(
  userId: string,
  options: { hidden: boolean },
): Promise<ColleagueListItem[]> {
  const query = new URLSearchParams({
    hidden: String(options.hidden),
    limit: String(COLLEAGUE_LIMIT),
  });
  const { data } = await apiFetchJson<{ data: ColleagueListItem[] }>(
    `/users/${userId}/colleagues?${query.toString()}`,
  );
  return data;
}

/** The Team page: the colleagues the user has 1:1s with, or the ones they hid. */
export async function fetchOneOnOneColleagues(
  userId: string,
  options: { hidden: boolean },
): Promise<OneOnOneColleagueItem[]> {
  const query = new URLSearchParams({
    hidden: String(options.hidden),
    limit: String(COLLEAGUE_LIMIT),
  });
  const { data } = await apiFetchJson<{ data: OneOnOneColleagueItem[] }>(
    `/users/${userId}/one-on-one-colleagues?${query.toString()}`,
  );
  return data;
}

/** One colleague's page: who they are and every 1:1 with them, either side of now. */
export async function fetchOneOnOneColleague(
  userId: string,
  colleagueId: string,
): Promise<OneOnOneColleagueDetail> {
  return apiFetchJson<OneOnOneColleagueDetail>(
    `/users/${userId}/one-on-one-colleagues/${colleagueId}`,
  );
}

/** The same read, as the route loader and the page share it. */
export function oneOnOneColleagueQuery(userId: string, colleagueId: string) {
  return {
    queryKey: ['oneOnOneColleague', userId, colleagueId] as const,
    queryFn: () => fetchOneOnOneColleague(userId, colleagueId),
  };
}

/** Take someone off the Team page, or put them back. Nothing is deleted. */
export async function setColleagueHidden(
  userId: string,
  colleagueId: string,
  hidden: boolean,
): Promise<void> {
  await apiFetch(`/users/${userId}/colleagues/${colleagueId}/hidden`, {
    method: 'PUT',
    body: JSON.stringify({ hidden }),
  });
}

/** Record that the one-time "who is your manager?" is answered, so it is not asked again. */
export async function answerManagerQuestion(userId: string): Promise<void> {
  await apiFetch(
    `/users/${userId}/one-on-one-colleagues/actions/answer-manager-question`,
    {
      method: 'POST',
      body: '{}',
    },
  );
}

/** Say who a colleague on the Team page is to the user. */
export async function setColleagueRelationship(
  userId: string,
  colleagueId: string,
  relationship: ColleagueRelationship,
): Promise<void> {
  await apiFetch(`/users/${userId}/one-on-one-colleagues/${colleagueId}/relationship`, {
    method: 'PUT',
    body: JSON.stringify({ relationship }),
  });
}
